import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Document, Page } from 'react-pdf';
import { LoadingSpinner } from '../common';
import {
  DocumentsUploaderErrorModalActions,
  DocumentsUploaderErrorModalIcon,
  DocumentsUploaderErrorModalInformation,
} from './DocumentsUploaderErrorModal';
import { errors } from './errors';

interface MultiplePreviewModalProps {
  isLoadingUpload: boolean;
  documents: File[];
  isOpen: boolean;
  handleCloseMultiplePreview: () => void;
  handleSendDocuments: () => Promise<void>;
}

// Loading spinner component
function MultiplePreviewModalLoading() {
  return (
    <div className="previsualization-container">
      <LoadingSpinner />
    </div>
  );
}

// Error display component
function MultiplePreviewModalError() {
  return (
    <div>
      <DocumentsUploaderErrorModalIcon />
      <DocumentsUploaderErrorModalInformation
        reason={errors.PDF_RENDERING_ERROR.reason}
        description={errors.PDF_RENDERING_ERROR.description}
      />
      <DocumentsUploaderErrorModalActions onRetry={() => {}} />
    </div>
  );
}

// Header props interface
interface MultiplePreviewModalHeaderProps {
  index: number;
  total: number;
}

// Header component
function MultiplePreviewModalHeader({ index, total }: MultiplePreviewModalHeaderProps) {
  return (
    <header className="mb-4">
      <img
        className="mb-4"
        src="/static/img/icons/danger-triangle.svg"
        width={64}
        alt=""
      />
      <h3 className="fs-4 text-primary mb-4">
        Revisa y confirma que
        <br />
        los documentos estén correctos
      </h3>
      <div className="fw-bold text-subtitle">
        {index + 1}
        /
        {total}
      </div>
    </header>
  );
}

// PDF Document preview props interface
interface MultiplePreviewModalDocumentProps {
  document: File;
  pages: number;
  setPages: (pages: number) => void;
  setIsRenderingError: (v: boolean) => void;
}

// PDF Document preview component
function MultiplePreviewModalDocument({
  document,
  pages,
  setPages,
  setIsRenderingError,
}: MultiplePreviewModalDocumentProps) {
  return (
    <div className="previsualization-container">
      <Document
        file={URL.createObjectURL(document)}
        onLoadSuccess={({ numPages }) => setPages(numPages)}
        onLoadError={() => setIsRenderingError(true)}
        loading={<MultiplePreviewModalLoading />}
      >
        {Array.from(new Array(pages), (el, i) => (
          <Page
            renderTextLayer={false}
            renderAnnotationLayer={false}
            key={`page_${i + 1}`}
            pageNumber={i + 1}
          />
        ))}
      </Document>
    </div>
  );
}

// Footer (navigation buttons) props interface
interface MultiplePreviewModalFooterProps {
  isLoadingUpload: boolean;
  onPrev: () => void;
  onNext: () => void;
  backText: string;
  nextText: string;
}

// Footer (navigation buttons) component
function MultiplePreviewModalFooter({
  isLoadingUpload,
  onPrev,
  onNext,
  backText,
  nextText,
}: MultiplePreviewModalFooterProps) {
  return (
    <footer className="row justify-content-center g-4">
      <div className="col-md-3 col-lg-2">
        <button
          disabled={isLoadingUpload}
          onClick={onPrev}
          type="button"
          className="btn btn-outline-primary w-100"
        >
          {backText}
        </button>
      </div>
      <div className="col-md-3 col-lg-2">
        <button
          disabled={isLoadingUpload}
          onClick={onNext}
          type="button"
          className="btn btn-primary w-100"
        >
          {isLoadingUpload ? <LoadingSpinner /> : <span>{nextText}</span>}
        </button>
      </div>
    </footer>
  );
}

// Main modal component
export function MultiplePreviewModal({
  documents,
  isOpen,
  isLoadingUpload,
  handleCloseMultiplePreview,
  handleSendDocuments,
}: MultiplePreviewModalProps) {
  const [document, setDocument] = useState<File>();
  const [index, setIndex] = useState(0);
  const [pages, setPages] = useState(0);
  const [isRenderingError, setIsRenderingError] = useState(false);

  const isLastDocument = index + 1 === documents.length;
  const isFirstDocument = index === 0;

  useEffect(() => {
    if (isOpen) {
      setDocument(documents[index]);
      setIsRenderingError(false);
    } else {
      setDocument(undefined);
      setIndex(0);
      setIsRenderingError(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setDocument(documents[index]);
    setIsRenderingError(false);
  }, [index]);

  const handleNextDocument = async () => {
    try {
      if (isLastDocument) {
        await handleSendDocuments();
        return;
      }
      const next = index + 1;
      if (next < documents.length) setIndex(next);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePrevDocument = () => {
    if (isFirstDocument) {
      handleCloseMultiplePreview();
      return;
    }
    const prev = index - 1;
    if (prev >= 0) setIndex(prev);
  };

  const buttonsNavigationTexts = useMemo(
    () => ({
      back: isFirstDocument ? 'Cancelar' : 'Anterior',
      next: isLastDocument ? 'Enviar' : 'Siguiente',
    }),
    [isFirstDocument, isLastDocument]
  );

  return (
    <Modal show={isOpen} onHide={handleCloseMultiplePreview} size={isRenderingError ? 'sm' : 'xl'} centered>
      {/* We expect that when there is an error, force the user to reload the page
      and try again.
      So we don't need to show the close button.
      */}
      <Modal.Header className="p-6" closeLabel="Cerrar" closeButton={!isRenderingError} />
      <Modal.Body className="px-6 pt-0 pb-8 text-center">
        {isRenderingError ? (
          <MultiplePreviewModalError />
        ) : (
          <>
            <MultiplePreviewModalHeader index={index} total={documents.length} />
            {document && (
              <MultiplePreviewModalDocument
                document={document}
                pages={pages}
                setPages={setPages}
                setIsRenderingError={setIsRenderingError}
              />
            )}
            <MultiplePreviewModalFooter
              isLoadingUpload={isLoadingUpload}
              onPrev={handlePrevDocument}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onNext={() => handleNextDocument().catch(console.error)}
              backText={buttonsNavigationTexts.back}
              nextText={buttonsNavigationTexts.next}
            />
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
